import {
  BROWSER_RESIZE_MAX_BYTES,
  IMAGES_INPUT_MAX_BYTES,
  IMAGES_INPUT_MAX_EDGE_PX,
  IMAGES_INPUT_MAX_PIXELS,
  isApiErrorCode,
} from "@ckgo2/shared";
import type { MediaFolder } from "@ckgo2/shared/types";
import i18n from "@/i18n";
import { apiErrorText } from "@/lib/apiErrorText";
import { formatFileSize } from "@/lib/mediaFormat";
import type { BatchFile, BatchOptions, BatchUpload } from "./batchUpload.types";

/**
 * Čisté funkce panelu dávky (`BatchUploadPanel`): co panel ukáže a co na modelu dávky
 * (`useBatchUpload`) zavolá. Stav dávky, otisky, kontrolu duplicit a nahrávání počítá model;
 * panel z něj jen odvozuje stav a nabídku akcí.
 */

/** Režim panelu: stránka Média, nebo záložka Nahrát okna výběru fotek. */
export type BatchPanelMode = "page" | "picker";

/** Stav panelu: prázdný, připravený (soubory bez startu), běží, hotovo. */
export type BatchPanelState = "idle" | "ready" | "running" | "finished";

/**
 * `running` má přednost (i opakování po dávce je běh); bez souborů je panel prázdný, i když
 * prohlížeč nějaké soubory odmítl; soubory bez startu čekají na „Nahrát N souborů“.
 */
export function batchPanelState(
  b: Pick<BatchUpload, "files" | "running" | "startedAt">,
): BatchPanelState {
  if (b.running) return "running";
  if (b.files.length === 0) return "idle";
  if (b.startedAt === null) return "ready";
  return "finished";
}

/**
 * Volby startu dávky z voleb panelu. Existující fotka převezme země a zdroj dávky jen na
 * stránce Média (F7.8); výběr fotek posílá vybrané fotky kvůli štítku „V galerii“.
 */
export function batchStartOptions(i: {
  mode: BatchPanelMode;
  folder: MediaFolder;
  countryIds: readonly string[];
  source: string;
  selectedIds?: readonly string[];
}): BatchOptions {
  const source = i.source.trim();
  const opts: BatchOptions = {
    folder: i.folder,
    countryIds: [...new Set(i.countryIds)],
    source: source === "" ? null : source,
    adoptExisting: i.mode === "page",
  };
  if (i.mode === "picker") opts.selectedIds = [...(i.selectedIds ?? [])];
  return opts;
}

/**
 * Co řádek souboru v panelu ukáže: štítek „V galerii“ a akce. `existingMediaId` je cíl odkazu
 * „Otevřít fotku“ (existující fotka u duplicity), `null` = odkaz chybí.
 */
export interface BatchFileView {
  remove: boolean;
  retry: boolean;
  restore: boolean;
  existingMediaId: string | null;
  inGallery: boolean;
}

/**
 * Pohled řádku podle stavu souboru a panelu. Odebrat jde jen před startem a ne u souboru, který
 * se právě připravuje nebo nahrává. Trvalá chyba (`error-permanent`) nikdy nemá „Zkusit znovu“:
 * prohlížeč ani server by ji podruhé nepřijali (Pojmy řezu).
 */
export function batchFileView(
  f: BatchFile,
  mode: BatchPanelMode,
  state: BatchPanelState,
): BatchFileView {
  const busy = f.status === "preparing" || f.status === "uploading";
  const duplicateWithLink = f.status === "duplicate-live" || f.status === "duplicate-in-batch";
  const galleryLabel = f.status === "done" || f.status === "duplicate-live";
  return {
    remove: state === "ready" && !busy && f.status !== "done",
    retry: f.status === "error",
    restore: f.status === "duplicate-trash",
    existingMediaId: duplicateWithLink && f.mediaId ? f.mediaId : null,
    inGallery: galleryLabel && mode === "picker" && f.inGallery === true,
  };
}

/**
 * „Zkusit znovu vše chybné“ jen při opakovatelné chybě. Rozhoduje stav souborů, ne
 * `summary.errors`: ten počítá i trvalé chyby, které se znovu neposílají.
 */
export function canRetryAll(files: readonly BatchFile[]): boolean {
  return files.some((f) => f.status === "error");
}

/**
 * Text chyby souboru — jediné místo, kde panel chybu převádí na text. Kód z registru se
 * překládá v okamžiku vykreslení (`apiErrorText`, jazyk adminu), proto má přednost před
 * `error`, který je text z okamžiku chyby.
 */
export function batchFileErrorText(f: BatchFile): string {
  if (f.permanentReason === "tif-unsupported") return i18n.t("mediaUpload.permanent.tif");
  if (f.permanentReason === "heic-too-large") {
    // Práh v téže jednotce jako velikost v řádku souboru (`formatFileSize`).
    return i18n.t("mediaUpload.permanent.heic", {
      limit: formatFileSize(BROWSER_RESIZE_MAX_BYTES),
    });
  }
  if (isApiErrorCode(f.errorCode)) {
    return apiErrorText(f.errorCode, { file: f.name, ...f.errorParams });
  }
  if (f.permanentReason === "server-limits") {
    return apiErrorText("media_exceeds_image_limits", {
      file: f.name,
      maxMb: IMAGES_INPUT_MAX_BYTES / 1024 / 1024,
      maxPx: IMAGES_INPUT_MAX_EDGE_PX,
      maxMp: IMAGES_INPUT_MAX_PIXELS / 1_000_000,
    });
  }
  if (f.error) return f.error;
  return i18n.t("mediaUpload.errorGeneric");
}

/**
 * Zneplatnit nabídku dřívějších zdrojů (`MEDIA_SOURCES_KEY`) nad snímkem modelu dávky? Jen po
 * doběhnutí (`finishedAt` je, nic neběží; opakování po dávce je zase běh a zneplatní až jeho
 * konec) a jen když dávka nesla zdroj a aspoň jedné fotce ho mohla zapsat (nová fotka nebo
 * existující, která zdroj převzala).
 */
export function shouldRefreshSources(
  b: Pick<BatchUpload, "finishedAt" | "running" | "summary">,
  source: string,
): boolean {
  if (b.finishedAt === null || b.running) return false;
  const s = b.summary;
  return source.trim() !== "" && s !== null && s.done + s.existing > 0;
}
